import { useEffect, useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { X, Send, Reply as ReplyIcon } from "lucide-react";

/**
 * Owner-facing reply thread modal.
 * Attaches to an existing item (notice/invoice/application) already visible in the owner dashboard.
 */
export default function ReplyThread({ open, onClose, entityType, entityId, entityTitle }) {
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    if (!open) return;
    try {
      const { data } = await api.get(`/portal/replies/${entityType}/${entityId}`);
      setThread(data);
    } catch { setThread(null); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [open, entityType, entityId]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [thread]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post("/portal/replies", { entity_type: entityType, entity_id: entityId, text: text.trim() });
      setText("");
      toast.success("Reply sent to the committee");
      await load();
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl border border-stone-200 flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()} data-testid="reply-thread-modal">
        <div className="flex items-start justify-between px-6 py-4 border-b border-stone-200">
          <div>
            <div className="text-xs uppercase tracking-widest text-terracotta font-semibold flex items-center gap-1"><ReplyIcon size={12}/> Reply to committee</div>
            <div className="editorial text-lg mt-0.5">{entityTitle || `${entityType} #${String(entityId).slice(0, 8)}`}</div>
          </div>
          <button onClick={onClose} className="p-2 text-stone-500 hover:text-ink" data-testid="reply-close"><X size={20}/></button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-4 space-y-3 min-h-[240px] bg-sand-50">
          {!thread || thread.messages?.length === 0 ? (
            <div className="text-center text-stone-500 text-sm py-10">
              No messages yet. Type your reply below — it will be delivered privately to the committee.
            </div>
          ) : thread.messages.map((m) => (
            <div key={m._id || m.created_at} className={`flex ${m.sender_kind === "owner" ? "justify-end" : "justify-start"}`} data-testid={`reply-msg-${m._id || m.created_at}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.sender_kind === "owner" ? "bg-terracotta text-white" : "bg-white border border-stone-200 text-ink"}`}>
                <div className={`text-[11px] uppercase tracking-widest mb-1 ${m.sender_kind === "owner" ? "text-white/70" : "text-stone-500"}`}>{m.sender_name} · {(m.created_at || "").slice(0, 16).replace("T", " ")}</div>
                <div className="whitespace-pre-wrap leading-relaxed">{m.text}</div>
              </div>
            </div>
          ))}
          {thread?.status === "resolved" && (
            <div className="text-center text-xs uppercase tracking-widest text-stone-500 py-2">— Thread closed by committee —</div>
          )}
        </div>

        <form onSubmit={submit} className="border-t border-stone-200 p-3 flex items-end gap-2">
          <textarea rows="2" value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Write your reply…"
            data-testid="reply-input"
            disabled={thread?.status === "resolved"}
            className="flex-1 rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 resize-none"/>
          <button type="submit" disabled={busy || !text.trim() || thread?.status === "resolved"}
            data-testid="reply-send"
            className="inline-flex items-center gap-1 rounded-full bg-terracotta hover:bg-terracotta-hover disabled:opacity-50 text-white px-4 py-2.5 text-sm font-medium">
            <Send size={14}/> Send
          </button>
        </form>
      </div>
    </div>
  );
}


/**
 * Small "Reply" button that opens the ReplyThread modal. Reusable across owner list rows.
 */
export function ReplyButton({ entityType, entityId, entityTitle, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid={testid || `reply-btn-${entityId}`}
        className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 hover:bg-sand-100 text-stone-700 px-3 py-1.5 text-xs font-medium">
        <ReplyIcon size={12}/> Reply
      </button>
      <ReplyThread open={open} onClose={() => setOpen(false)} entityType={entityType} entityId={entityId} entityTitle={entityTitle}/>
    </>
  );
}
